/**
 * CL-NEG-01 Task 10 — the two small state lists the cart lock and Recreate cart ask.
 *
 * Built from `states` in transactionProcessPurchase.js rather than hand-copied, so this cannot
 * drift from the graph the process actually runs.
 */

import { states, transitions } from '../../transactions/transactionProcessPurchase';
import { PURCHASE_PROCESS_NAME } from '../../transactions/transaction';

// CL-NEG-01 requirement 13 — an open offer freezes this ONE seller's group. NOT because the offer
// holds that stock: under D12 it holds nothing. Because the offer is PRICED from a frozen
// `protectedData.cart` snapshot (offer-initiate.js writes it, runPaymentJoin re-reads it), so a
// basket edited underneath a live negotiation would make this page and the offer describe two
// different orders — and "Change items" would silently mean "make a different offer". This list
// is the only question the lock asks: is the negotiation that priced this group still open?
// `canceled` is included: a cancelled order is refunded and over, so the group it locked has
// nothing left to protect.
export const OFFER_CLOSED_STATES = [
  `state/${states.OFFER_DECLINED}`,
  `state/${states.OFFER_EXPIRED}`,
  `state/${states.PAYMENT_EXPIRED}`,
  `state/${states.CANCELED}`,
];

// "Did the buyer lose a basket without buying it?" — deliberately NOT the same list as
// OFFER_CLOSED_STATES: a transaction in `purchased` or later already took those goods at payment
// (and the buyer's own basket for that seller was already cleared by checkout, so there is no
// group left to unlock — Task 11 §E). `canceled` is excluded here on purpose, unlike above: a
// cancelled order was PAID and refunded, so offering to rebuild it invites a second purchase of
// goods that may be on their way back.
export const RECREATE_CART_STATES = [
  `state/${states.OFFER_DECLINED}`,
  `state/${states.OFFER_EXPIRED}`,
  `state/${states.PAYMENT_EXPIRED}`,
];

// Task 11 §A step 2 — every transition the offer phase owns, built from the purchase graph's own
// `transitions` object rather than hand-copied strings, so a renamed transition cannot leave a
// stale one behind here.
const OFFER_TRANSITION_NAMES = [
  transitions.BUYER_MAKE_OFFER,
  transitions.SELLER_MAKE_OFFER,
  transitions.SELLER_COUNTER_OFFER,
  transitions.BUYER_COUNTER_OFFER,
  transitions.SELLER_ACCEPT_OFFER,
  transitions.SELLER_DECLINE_OFFER,
  transitions.BUYER_WITHDRAW_OFFER,
  transitions.BUYER_DECLINE_OFFER,
  transitions.SELLER_WITHDRAW_OFFER,
  transitions.EXPIRE_BUYER_OFFER,
  transitions.EXPIRE_SELLER_OFFER,
  transitions.EXPIRE_AGREED_OFFER,
  transitions.REQUEST_PAYMENT_FROM_OFFER,
  transitions.REQUEST_PAYMENT_FROM_AGREED,
];

/**
 * "Does this transition belong to the offer phase's own privileged endpoint?" — the question
 * `TransactionPage.duck.js`'s `makeTransitionPayloadCreator` asks before deciding whether to call
 * `/api/offer-transition` or the template's own `transitionPrivileged`/plain SDK paths.
 *
 * Scoped to `default-purchase` on purpose: `default-negotiation` has its own, differently-shaped
 * counter-offer flow (`CUSTOMER_MAKE_COUNTER_OFFER` / `PROVIDER_MAKE_COUNTER_OFFER`, routed through
 * `transitionPrivileged` with `orderData`), and nothing here should change what that process does
 * merely because a purchase-process transition string happens to share a name.
 */
export const isOfferTransition = (processName, transitionName) =>
  processName === PURCHASE_PROCESS_NAME && OFFER_TRANSITION_NAMES.includes(transitionName);

// ActivityFeed per-transition messages (merged into stateData.transitionMessages by
// TransactionPage, the same idiom as tracking.js's TRACKING_TRANSITION_MESSAGES). Per-transition
// rather than per-next-state (ActivityFeed.js:111-113's default), because four different
// transitions land in `buyer-offer-pending` and "You made an offer of $2,800" must not read the
// same as "You countered at $2,800".
export const OFFER_TRANSITION_MESSAGES = [
  {
    transition: transitions.BUYER_MAKE_OFFER,
    translationId: 'TransactionPage.ActivityFeed.default-purchase.transition.buyer-make-offer',
  },
  {
    // CL-DISC-01 D1's opening move by the seller. Needs its own entry for the same reason
    // buyer-make-offer does — and for one more: `seller-offer-pending` is ALSO reached by
    // `seller-counter-offer`, so ActivityFeed's per-next-state default could not tell an opening
    // offer from a counter even if a `…default-purchase.seller-offer-pending` key existed. It
    // does not, which is why the feed rendered this transition's raw id until this entry.
    transition: transitions.SELLER_MAKE_OFFER,
    translationId: 'TransactionPage.ActivityFeed.default-purchase.transition.seller-make-offer',
  },
  {
    transition: transitions.SELLER_COUNTER_OFFER,
    translationId: 'TransactionPage.ActivityFeed.default-purchase.transition.seller-counter-offer',
  },
  {
    transition: transitions.BUYER_COUNTER_OFFER,
    translationId: 'TransactionPage.ActivityFeed.default-purchase.transition.buyer-counter-offer',
  },
  {
    transition: transitions.SELLER_ACCEPT_OFFER,
    translationId: 'TransactionPage.ActivityFeed.default-purchase.transition.seller-accept-offer',
  },
  {
    transition: transitions.SELLER_DECLINE_OFFER,
    translationId: 'TransactionPage.ActivityFeed.default-purchase.transition.seller-decline-offer',
  },
  {
    transition: transitions.BUYER_DECLINE_OFFER,
    translationId: 'TransactionPage.ActivityFeed.default-purchase.transition.buyer-decline-offer',
  },
  {
    transition: transitions.BUYER_WITHDRAW_OFFER,
    translationId: 'TransactionPage.ActivityFeed.default-purchase.transition.buyer-withdraw-offer',
  },
  {
    transition: transitions.SELLER_WITHDRAW_OFFER,
    translationId: 'TransactionPage.ActivityFeed.default-purchase.transition.seller-withdraw-offer',
  },
  {
    transition: transitions.EXPIRE_BUYER_OFFER,
    translationId: 'TransactionPage.ActivityFeed.default-purchase.transition.expire-offer',
  },
  {
    transition: transitions.EXPIRE_SELLER_OFFER,
    translationId: 'TransactionPage.ActivityFeed.default-purchase.transition.expire-offer',
  },
  {
    transition: transitions.EXPIRE_AGREED_OFFER,
    translationId: 'TransactionPage.ActivityFeed.default-purchase.transition.expire-agreed-offer',
  },
];
